import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert
} from '@mui/material';
import { Warning, GpsFixed, Edit, Refresh } from '@mui/icons-material';
import { getGPSAccuracySetting, shouldBlockForGPSAccuracy, GPS_ACCURACY_THRESHOLD } from '../utils/utils';

const GPSAccuracyDialog = ({ 
    open, 
    onRefreshGPS, 
    onManualEntry, 
    accuracy,
    onClose 
}) => {
    const gpsAccuracySetting = getGPSAccuracySetting();
    const isForced = shouldBlockForGPSAccuracy();
    
    // Don't show dialog if setting is "Off"
    if (gpsAccuracySetting === 'Off') {
        return null;
    }

    const handleRefreshGPS = () => {
        console.log('GPS Accuracy Dialog: Refreshing GPS and page...');
        // Full page refresh as specified in requirements
        window.location.reload();
    };

    const handleManualEntry = () => {
        console.log('GPS Accuracy Dialog: Manual entry selected');
        if (onManualEntry) {
            onManualEntry();
        }
        if (onClose) {
            onClose();
        }
    };

    // Get warning text based on mode and accuracy level
    const getWarningText = () => {
        const accuracyText = accuracy ? `±${Math.round(accuracy)}m` : 'poor';
        
        if (isForced) {
            return `WARNING: GPS accuracy is ${accuracyText}. This exceeds the required ±${GPS_ACCURACY_THRESHOLD}m threshold. Refresh location data, or request service through dispatch.`;
        } else {
            return `WARNING: GPS accuracy is ${accuracyText}. This exceeds the required ±${GPS_ACCURACY_THRESHOLD}m threshold. Refresh location data, or manually enter location.`;
        }
    };

    // Get severity level based on how far over threshold we are
    const getSeverityLevel = () => {
        if (!accuracy) return 'error';
        
        if (accuracy > GPS_ACCURACY_THRESHOLD * 3) { // >300m
            return 'error';
        } else if (accuracy > GPS_ACCURACY_THRESHOLD * 2) { // >200m
            return 'warning';
        } else {
            return 'warning';
        }
    };

    const getDialogTitle = () => {
        if (isForced) {
            return 'GPS Accuracy Required';
        }
        return 'GPS Accuracy Warning';
    };

    const getAccuracyStatus = () => {
        if (!accuracy) return 'Unknown';
        
        if (accuracy <= GPS_ACCURACY_THRESHOLD) {
            return 'Good';
        } else if (accuracy <= GPS_ACCURACY_THRESHOLD * 2) {
            return 'Poor';
        } else {
            return 'Very Poor';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isForced ? undefined : onClose} // Prevent closing in forced mode
            fullWidth
            maxWidth="sm"
            disableEscapeKeyDown={isForced} // Prevent escape key in forced mode
            PaperProps={{
                sx: { 
                    borderRadius: 2,
                    border: `2px solid ${getSeverityLevel() === 'error' ? '#f44336' : '#ff9800'}` // Red or orange border
                }
            }}
        >
            <DialogContent sx={{ py: 4, px: 3 }}>
                <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    textAlign="center"
                >
                    {/* Warning Icon */}
                    <Warning 
                        sx={{ 
                            fontSize: 60, 
                            color: getSeverityLevel() === 'error' ? 'error.main' : 'warning.main', 
                            mb: 2 
                        }} 
                    />
                    
                    {/* Dialog Title */}
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            mb: 2,
                            fontWeight: 'bold',
                            color: getSeverityLevel() === 'error' ? 'error.main' : 'warning.main'
                        }}
                    >
                        {getDialogTitle()}
                    </Typography>
                    
                    {/* Warning Text */}
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            mb: 3,
                            fontWeight: 'bold',
                            color: getSeverityLevel() === 'error' ? 'error.main' : 'warning.main',
                            lineHeight: 1.4
                        }}
                    >
                        {getWarningText()}
                    </Typography>

                    {/* Accuracy Information */}
                    <Alert severity={getSeverityLevel()} sx={{ mb: 3, width: '100%' }}>
                        <Typography variant="body2">
                            <strong>Current GPS Accuracy:</strong> {accuracy ? `±${Math.round(accuracy)}m` : 'Unknown'} ({getAccuracyStatus()})
                            <br />
                            <strong>Required Accuracy:</strong> ±{GPS_ACCURACY_THRESHOLD}m or better
                            <br />
                            <strong>GPS Setting:</strong> {gpsAccuracySetting} mode
                            <br />
                            {isForced ? (
                                <strong style={{ color: '#d32f2f' }}>Call entry is blocked until accuracy improves</strong>
                            ) : (
                                <strong style={{ color: '#ed6c02' }}>You may proceed manually or improve GPS accuracy</strong>
                            )}
                        </Typography>
                    </Alert>

                    {/* Mode-specific information */}
                    {isForced && (
                        <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                            <Typography variant="body2">
                                <strong>Forced GPS Mode:</strong> Your account requires high GPS accuracy. 
                                If GPS accuracy cannot be improved, please contact dispatch directly to request service.
                            </Typography>
                        </Alert>
                    )}

                    {/* Manual mode information */}
                    {!isForced && (
                        <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                            <Typography variant="body2">
                                <strong>Manual Override Available:</strong> You can proceed with manual location entry 
                                if GPS accuracy cannot be improved.
                            </Typography>
                        </Alert>
                    )}

                    {/* Tips for improving GPS accuracy */}
                    <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1, 
                        width: '100%',
                        textAlign: 'left'
                    }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Tips to improve GPS accuracy:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            • Move to an open area away from buildings<br />
                            • Ensure location services are enabled<br />
                            • Wait a few moments for GPS to stabilize<br />
                            • Try refreshing the page<br />
                            • Check that high accuracy location is enabled in device settings
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions 
                sx={{
                    px: 3,
                    pb: 3,
                    gap: 2,
                    justifyContent: 'center'
                }}
            >
                {/* Refresh GPS Button - Always present */}
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<Refresh />}
                    onClick={handleRefreshGPS}
                    size="large"
                    sx={{
                        minWidth: 160,
                        backgroundColor: '#4CAF50',
                        '&:hover': {
                            backgroundColor: '#45a049'
                        }
                    }}
                >
                    REFRESH GPS
                </Button>

                {/* Manual Entry Button - Only in Manual mode */}
                {!isForced && (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<Edit />}
                        onClick={handleManualEntry}
                        size="large"
                        sx={{
                            minWidth: 160,
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            '&:hover': {
                                borderColor: '#1565c0',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                    >
                        MANUAL ENTRY
                    </Button>
                )}
            </DialogActions>

            {/* Additional styling */}
            <style jsx global>{`
                .MuiDialog-paper {
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
                }
            `}</style>
        </Dialog>
    );
};

export default GPSAccuracyDialog;